/**
 * Mobile Portal Layout
 *
 * Purpose: Layout for SmartPay mobile/field teams (per PRD §6).
 * Responsive: mobile menu state lifted so Header menu button opens Sidebar.
 * Location: mobile-portal/src/components/layout/Layout.tsx
 */

import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';

const ROUTE_TITLES: Record<string, { title: string; subtitle?: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Mobile units & ATM overview' },
  '/field': { title: 'Field Hub', subtitle: 'Units, requests, map, activity' },
  '/field/units': { title: 'Field Hub', subtitle: 'Mobile units' },
  '/field/requests': { title: 'Field Hub', subtitle: 'Requests' },
  '/field/map': { title: 'Field Hub', subtitle: 'Map' },
  '/field/activity': { title: 'Field Hub', subtitle: 'Activity' },
  '/map': { title: 'Map', subtitle: 'Mobile units, NamPost offices, ATMs' },
  '/mobile-units': { title: 'Mobile Units', subtitle: 'View units, equipment, drivers; log activity' },
  '/atms': { title: 'ATMs', subtitle: 'View status, cash level; log maintenance' },
  '/requests': { title: 'Requests', subtitle: 'Equipment & trip requests (Ketchup approves)' },
  '/activity': { title: 'Activity', subtitle: 'Inspection, repair, service log' },
  '/settings': { title: 'Settings', subtitle: 'Portal preferences' },
  '/help': { title: 'Help', subtitle: 'Support and documentation' },
};

function getTitleForPath(pathname: string) {
  const normalized = pathname.replace(/\/$/, '') || '/';
  return ROUTE_TITLES[normalized] ?? { title: pathname.slice(1) || 'Dashboard', subtitle: '' };
}

export default function Layout() {
  const location = useLocation();
  const { title, subtitle } = getTitleForPath(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="flex min-h-screen w-full max-w-full overflow-x-hidden bg-background">
      <Sidebar isMobileOpen={mobileMenuOpen} onMobileOpenChange={setMobileMenuOpen} />
      <div className="flex flex-1 flex-col min-w-0 lg:ml-[260px] min-h-screen transition-all duration-300">
        <Header title={title} subtitle={subtitle} onMenuClick={() => setMobileMenuOpen(true)} />
        <main className="flex-1 min-w-0 p-4 sm:p-5 md:p-6 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
